import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';
import { parseAndValidateExif, parseBatchExif, type ExifValidationResult } from '@/lib/exifParser';

export interface EmpExecucao {
  id: string;
  contrato_id: string;
  planilha_item_id: string | null;
  cronograma_atividade_id: string | null;
  frente_id: string | null;
  equipe_id: string | null;
  data_execucao: string;
  quantidade_executada: number;
  unidade: string;
  tipo: 'execucao' | 'retrabalho';
  execucao_original_id: string | null;
  observacoes: string | null;
  localizacao_descricao: string | null;
  latitude: number | null;
  longitude: number | null;
  registrado_por: string;
  validado_por: string | null;
  validado_em: string | null;
  status: 'pendente' | 'validado' | 'rejeitado' | 'em_apontamento' | 'aguardando_qualidade' | 'aguardando_planejamento' | 'disponivel_para_medicao' | 'vinculada_a_boletim' | 'cancelado';
  qualidade_status: 'pendente' | 'aprovada' | 'rejeitada' | null;
  qualidade_por: string | null;
  qualidade_em: string | null;
  qualidade_observacoes: string | null;
  certificado_evidencia_id: string | null;
  motivo_rejeicao: string | null;
  dados_extras: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // joined
  item_descricao?: string;
  item_codigo?: string;
}

export interface EmpEvidencia {
  id: string;
  contrato_id: string;
  execucao_id: string | null;
  tipo: string;
  storage_path: string;
  nome_arquivo: string;
  tamanho_bytes: number | null;
  mime_type: string | null;
  descricao: string | null;
  categoria: string | null;
  exif_data: Record<string, unknown> | null;
  exif_latitude: number | null;
  exif_longitude: number | null;
  exif_data_hora: string | null;
  exif_dispositivo: string | null;
  registrado_por: string;
  created_at: string;
}

export interface ExecucaoInsert {
  contrato_id: string;
  planilha_item_id?: string | null;
  frente_id: string; // obrigatório para novas execuções
  data_execucao: string;
  quantidade_executada: number;
  unidade: string;
  tipo?: 'execucao' | 'retrabalho';
  execucao_original_id?: string | null;
  observacoes?: string | null;
  localizacao_descricao?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

// --- Hook: list executions for a contract ---
export function useEmpExecucoes(contratoId: string) {
  const { data: execucoes = [], isLoading } = useQuery({
    queryKey: ['emp-execucoes', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('data_execucao', { ascending: false });
      if (error) throw error;
      return data as EmpExecucao[];
    },
    enabled: !!contratoId,
  });

  return { execucoes, isLoading };
}

// --- Hook: list evidências for an execução (excludes soft-deleted) ---
export function useEmpEvidencias(execucaoId: string | null) {
  const { data: evidencias = [], isLoading } = useQuery({
    queryKey: ['emp-evidencias', execucaoId],
    queryFn: async () => {
      if (!execucaoId) return [];
      const { data, error } = await supabase
        .from('emp_evidencias')
        .select('*')
        .eq('execucao_id', execucaoId)
        .eq('excluido', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as EmpEvidencia[];
    },
    enabled: !!execucaoId,
  });

  return { evidencias, isLoading };
}

// --- Hook: CRUD mutations ---
export function useEmpExecucaoMutations(contratoId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const createExecucao = useMutation({
    mutationFn: async (input: ExecucaoInsert) => {
      if (!user) throw new Error('Não autenticado');
      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .insert({
          ...input,
          registrado_por: user.id,
          status: 'em_apontamento',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emp-execucoes', contratoId] });
      toast({ title: 'Execução registrada com sucesso' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao registrar execução', description: e.message, variant: 'destructive' }),
  });

  /**
   * Transition execution state via backend state machine (RPC).
   * All status changes MUST go through this — direct UPDATE is blocked by trigger.
   */
  const transicionarExecucao = useMutation({
    mutationFn: async ({ id, novoStatus, observacoes, motivoRejeicao, certificadoEvidenciaId }: {
      id: string;
      novoStatus: string;
      observacoes?: string;
      motivoRejeicao?: string;
      certificadoEvidenciaId?: string;
    }) => {
      const { data, error } = await supabase.rpc('emp_transicionar_execucao', {
        p_execucao_id: id,
        p_novo_status: novoStatus,
        p_observacoes: observacoes || null,
        p_motivo_rejeicao: motivoRejeicao || null,
        p_certificado_evidencia_id: certificadoEvidenciaId || null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (result: any) => {
      qc.invalidateQueries({ queryKey: ['emp-execucoes', contratoId] });
      qc.invalidateQueries({ queryKey: ['quality-gate-queue'] });
      qc.invalidateQueries({ queryKey: ['quality-gate-history'] });
      qc.invalidateQueries({ queryKey: ['planning-gate-queue'] });
      const novoStatus = result?.para || '';
      const isIdempotent = result?.idempotent === true;
      if (isIdempotent) {
        toast({ title: 'Status já atualizado', description: 'A execução já se encontra neste status.' });
      } else {
        toast({ title: `Transição realizada: ${novoStatus}` });
      }
      // Auto-trigger EVM recalculation when reaching disponivel_para_medicao
      if (novoStatus === 'disponivel_para_medicao' && !isIdempotent) {
        supabase.functions.invoke('calcular-evm-indicadores', {
          body: { contrato_id: contratoId },
        }).then(() => {
          qc.invalidateQueries({ queryKey: ['emp-indicadores', contratoId] });
        }).catch(err => console.warn('EVM auto-calc failed:', err));
      }
    },
    onError: (e: Error) => {
      // Refresh data on error to show actual status from DB
      qc.invalidateQueries({ queryKey: ['emp-execucoes', contratoId] });
      toast({ title: 'Erro na transição', description: e.message, variant: 'destructive' });
    },
  });

  const uploadEvidencia = useMutation({
    mutationFn: async ({ execucaoId, file, descricao, expectedLat, expectedLng, tipo, categoria }: {
      execucaoId: string;
      file: File;
      descricao?: string;
      expectedLat?: number;
      expectedLng?: number;
      tipo?: string;
      categoria?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');

      const isPdf = file.type === 'application/pdf';
      let exifResult: ExifValidationResult = { warnings: [], raw: null };

      // Only parse EXIF for images, skip for PDFs
      if (!isPdf) {
        exifResult = await parseAndValidateExif(file, {
          expectedLatitude: expectedLat,
          expectedLongitude: expectedLng,
          maxDistanceKm: 50,
          maxAgeDays: 7,
        });

        for (const w of exifResult.warnings) {
          if (w.severity === 'error') {
            toast({ variant: 'destructive', title: 'Alerta EXIF', description: w.message });
          } else if (w.severity === 'warn') {
            toast({ variant: 'default', title: 'Aviso EXIF', description: w.message });
          }
        }
      }

      const ext = file.name.split('.').pop() || (isPdf ? 'pdf' : 'jpg');
      const path = `${contratoId}/${execucaoId}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('emp-evidencias')
        .upload(path, file, { contentType: file.type });
      if (uploadError) throw uploadError;

      const insertPayload: any = {
        contrato_id: contratoId,
        execucao_id: execucaoId,
        tipo: tipo || (isPdf ? 'documento' : 'foto'),
        storage_path: path,
        nome_arquivo: file.name,
        tamanho_bytes: file.size,
        mime_type: file.type,
        descricao: descricao || null,
        categoria: categoria || 'execucao',
        exif_data: exifResult.raw || null,
        exif_latitude: exifResult.latitude || null,
        exif_longitude: exifResult.longitude || null,
        exif_data_hora: exifResult.dateTime || null,
        exif_dispositivo: exifResult.device || null,
        registrado_por: user.id,
      };

      const { error: insertError } = await supabase
        .from('emp_evidencias')
        .insert(insertPayload);
      if (insertError) throw insertError;

      return exifResult;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['emp-evidencias', vars.execucaoId] });
      toast({ title: 'Evidência enviada' });
    },
    onError: (e: Error) => toast({ title: 'Erro no upload', description: e.message, variant: 'destructive' }),
  });

  /**
   * Upload multiple files in batch with EXIF validation
   */
  const uploadBatchEvidencias = useMutation({
    mutationFn: async ({ execucaoId, files, expectedLat, expectedLng }: {
      execucaoId: string;
      files: File[];
      expectedLat?: number;
      expectedLng?: number;
    }) => {
      if (!user) throw new Error('Não autenticado');

      const results: ExifValidationResult[] = [];
      for (const file of files) {
        const result = await uploadEvidencia.mutateAsync({
          execucaoId,
          file,
          expectedLat,
          expectedLng,
        });
        results.push(result);
      }
      return results;
    },
  });

  /**
   * Trigger EVM recalculation for this contract
   */
  const recalcularEVM = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('calcular-evm-indicadores', {
        body: { contrato_id: contratoId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emp-indicadores', contratoId] });
      toast({ title: 'Indicadores EVM recalculados' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao calcular EVM', description: e.message, variant: 'destructive' }),
  });

  /**
   * Soft-delete an evidence with mandatory justification
   */
  const excluirEvidencia = useMutation({
    mutationFn: async ({ evidenciaId, execucaoId, justificativa }: {
      evidenciaId: string;
      execucaoId: string;
      justificativa: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      if (!justificativa || justificativa.trim().length < 10) {
        throw new Error('Justificativa obrigatória (mínimo 10 caracteres)');
      }
      const { error } = await supabase
        .from('emp_evidencias')
        .update({
          excluido: true,
          excluido_por: user.id,
          excluido_em: new Date().toISOString(),
          excluido_justificativa: justificativa.trim(),
        })
        .eq('id', evidenciaId);
      if (error) throw error;
      return { evidenciaId, execucaoId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['emp-evidencias', result.execucaoId] });
      toast({ title: 'Evidência removida', description: 'A exclusão foi registrada na auditoria.' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao excluir evidência', description: e.message, variant: 'destructive' }),
  });

  return { createExecucao, transicionarExecucao, uploadEvidencia, uploadBatchEvidencias, recalcularEVM, excluirEvidencia };
}

// --- Helper: get signed URL for evidence ---
export async function getEvidenciaUrl(storagePath: string): Promise<string | null> {
  const { data } = await supabase.storage
    .from('emp-evidencias')
    .createSignedUrl(storagePath, 3600);
  return data?.signedUrl || null;
}

// Re-export ExifValidationResult for consumers
export type { ExifValidationResult } from '@/lib/exifParser';
export { parseAndValidateExif, parseBatchExif } from '@/lib/exifParser';
